import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useLang } from "../LanguageContext";
import { formatApiError } from "../utils";

export default function Signup() {
  const { t } = useLang();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", date_of_birth: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form.name, form.email, form.password, form.date_of_birth || undefined);
      nav("/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="signup-page" className="pt-32 min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <p className="qw-overline mb-4 text-center">Membership</p>
        <h1 className="font-serif text-4xl md:text-5xl text-foreground text-center mb-4 font-light">
          {t.auth.signupTitle}
        </h1>
        <p className="text-center text-sm text-muted-foreground mb-12">{t.auth.signupSubtitle}</p>
        <form onSubmit={submit} className="space-y-8" data-testid="signup-form">
          <div>
            <label className="qw-overline block mb-2">{t.auth.name}</label>
            <input
              data-testid="signup-name-input" required
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="qw-input"
            />
          </div>
          <div>
            <label className="qw-overline block mb-2">{t.auth.email}</label>
            <input
              data-testid="signup-email-input" required type="email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="qw-input"
            />
          </div>
          <div>
            <label className="qw-overline block mb-2">{t.auth.password}</label>
            <input
              data-testid="signup-password-input" required type="password" minLength={6}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="qw-input"
            />
            <p className="text-[0.65rem] tracking-wider uppercase text-muted-foreground mt-2">{t.auth.passwordHint}</p>
          </div>
          <div>
            <label className="qw-overline block mb-2">{t.auth.dob}</label>
            <input
              data-testid="signup-dob-input" type="date"
              value={form.date_of_birth}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              className="qw-input"
            />
            <p className="text-[0.65rem] tracking-wider uppercase text-primary/80 mt-2">🎁 {t.auth.dobHint}</p>
          </div>
          {error && <p data-testid="signup-error" className="text-sm text-destructive">{error}</p>}
          <button
            type="submit" disabled={busy}
            data-testid="signup-submit-btn"
            className="qw-btn qw-btn-primary w-full justify-center disabled:opacity-60"
          >
            {busy ? t.auth.signingUp : t.auth.signup}
            {!busy && <ArrowUpRight size={14} strokeWidth={1.2} />}
          </button>
          <p className="text-center text-sm text-muted-foreground pt-4">
            {t.auth.haveAccount}{" "}
            <Link to="/login" className="qw-link-underline text-primary">{t.auth.loginHere}</Link>
          </p>
        </form>
      </div>
    </main>
  );
}
